import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import FloatingConfetti from './FloatingConfetti';
import Fireworks from './Fireworks';
import FestiveBanner from './FestiveBanner';
import SnowEffect from './SnowEffect';
import CountdownTimer from './CountdownTimer';
import { newYear2025Theme } from './themes/NewYear2025';
import { diwaliTheme } from './themes/Diwali';
import { christmasTheme } from './themes/Christmas';

const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext);

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const THEME_CONFIGS = {
  newyear2025: newYear2025Theme,
  diwali: diwaliTheme,
  christmas: christmasTheme
};

export const FestiveThemeProvider = ({ children, currentPage = 'customer' }) => {
  const [themeData, setThemeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const fetchActiveTheme = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/festival-themes/active`);
      const data = response.data;
      
      if (data.enabled && data.theme) {
        setThemeData(data.theme);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching theme:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveTheme();
  }, [fetchActiveTheme]);

  if (loading) {
    return <>{children}</>;
  }

  const themeConfig = themeData ? THEME_CONFIGS[themeData.id] : null;

  // Check if theme should apply to current page
  const shouldApply = themeData?.apply_to?.includes(currentPage) || 
                      themeData?.apply_to?.includes('all');

  if (!themeData || !themeConfig || !shouldApply) {
    return (
      <ThemeContext.Provider value={{ theme: null }}>
        {children}
      </ThemeContext.Provider>
    );
  }

  const intensity = themeData.intensity || 'medium';

  return (
    <ThemeContext.Provider value={{ theme: themeData, config: themeConfig }}>
      {/* Banner */}
      {themeData.show_banner && themeConfig.banner?.enabled && !bannerDismissed && (
        <FestiveBanner
          text={themeConfig.banner.text}
          backgroundColor={themeConfig.banner.backgroundColor}
          onClose={() => setBannerDismissed(true)}
        />
      )}

      {/* Snow Effect (New Year specific) */}
      {themeConfig.snow?.enabled && (
        <SnowEffect
          intensity={intensity}
          color={themeConfig.snow.color}
        />
      )}

      {/* Confetti */}
      {themeData.show_confetti && themeConfig.confetti?.enabled && (
        <FloatingConfetti
          colors={themeConfig.confetti.colors}
          count={themeConfig.confetti.count}
          intensity={intensity}
        />
      )}

      {/* Fireworks */}
      {themeData.show_fireworks && themeConfig.fireworks?.enabled && (
        <Fireworks
          colors={themeConfig.confetti?.colors || ['#FFD700', '#C0C0C0']}
          interval={themeConfig.fireworks.interval}
        />
      )}

      {/* Countdown Timer (New Year specific) */}
      {themeConfig.countdown?.enabled && (
        <CountdownTimer
          targetDate={themeConfig.countdown.targetDate}
        />
      )}

      {children}
    </ThemeContext.Provider>
  );
};

export default FestiveThemeProvider;
